import { useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { DotsLoader } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import AnimatedGradientText from "app/pages/sales-intelligence/modules/ai-agent/components/AnimatedGradientText/AnimatedGradientText";
import { MeetingAgentMessageWithErrorBoundary } from "app/pages/sales-intelligence/modules/ai-agent/components/MeetingAgentMessage/MeetingAgentMessageWithErrorBoundary";
import MessagesList from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/MessagesList";
import type { ChatUiContext } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/types";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import type { CompanyInfo } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import type { FeedbackReaction, ThreadMessage } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";

// Built lazily and cached: `translate` reads `window.i18n` at call time, so the
// context must not be evaluated at module load (i18n may not be ready yet).
let cachedUiContext: ChatUiContext | undefined;
export const getMeetingAgentUiContext = (): ChatUiContext =>
    (cachedUiContext ??= {
        chatTheme: "full-page",
        loader: <AnimatedGradientText text={translate("si.pages.ai_agent.reasoning_stage.default")} />,
        markdownComponent: MeetingAgentMessageWithErrorBoundary,
        streamingLoader: (
            <Box margin="12px 0 0 35px">
                <DotsLoader />
            </Box>
        ),
    });

type Props = {
    messages: ThreadMessage[];
    isLoading: boolean;
    onFeedback?: (runId: string, reaction: FeedbackReaction) => void;
    lastUserMessageRef: React.RefObject<HTMLDivElement | null>;
    lastAiMessageRef: React.RefObject<HTMLDivElement | null>;
    onCopy: () => void;
    renderLoaderCondition?: boolean;
    isStreamingLoaderVisible?: boolean;
};

const AIAgentMessagesList: React.FC<React.PropsWithChildren<Props>> = ({
    messages,
    isLoading,
    onFeedback,
    lastUserMessageRef,
    lastAiMessageRef,
    onCopy,
    renderLoaderCondition,
    isStreamingLoaderVisible,
}) => {
    const {
        params: { domain },
    } = useCurrentStateAndParams<{ domain: string }>();

    const queryClient = useQueryClient();
    const companyInfo = queryClient.getQueryData<CompanyInfo>(aiAgentQueryKeys.companyInfo(domain));

    const mdComponentProps = useMemo(
        () => ({
            companyLogoSrc: companyInfo?.favicon ?? "",
        }),
        [companyInfo?.favicon],
    );

    return (
        <MessagesList
            uiContext={getMeetingAgentUiContext()}
            isLoading={isLoading}
            messages={messages}
            lastUserMessageRef={lastUserMessageRef}
            lastAiMessageRef={lastAiMessageRef}
            mdComponentProps={mdComponentProps}
            onFeedback={onFeedback}
            onCopy={onCopy}
            renderLoaderCondition={renderLoaderCondition}
            isStreamingLoaderVisible={isStreamingLoaderVisible}
        />
    );
};

export default AIAgentMessagesList;
